/**
 * Rich text — a tiny, SAFE mini-markup for explanations (tooltips). The source is parsed into DOM nodes built with
 * createElement + textContent: nothing here parses HTML, so a `<script>` in the source is shown as text.
 *
 *   **bold**   !!red / danger!!   ^^amber / warning^^   ++green / ok++     (inline, may nest, never across a paragraph)
 *   a line starting with `• ` or `* `  -> a bulleted list item (consecutive lines make one list)
 *   a blank line                       -> a new paragraph; a single newline inside a paragraph is a line break
 * An unmatched marker stays literal text. The allow-list is closed: strong, three tone spans, p, ul, li, br.
 */

/** marker -> [tag, class]; the class is styled by the owner (`.ls-rich--danger` ...) */
const INLINE = Object.freeze({
  '**': ['strong', 'ls-rich--bold'],
  '!!': ['span', 'ls-rich--danger'],
  '^^': ['span', 'ls-rich--warn'],
  '++': ['span', 'ls-rich--ok'],
})
const MARKERS = Object.keys(INLINE)
const BULLET = /^(?:•|\*)\s+/

/** earliest marker with a closing partner; its position, content bounds */
function findPair(text) {
  let best = null
  for (const marker of MARKERS) {
    const open = text.indexOf(marker)
    if (open === -1 || (best && open >= best.open)) continue
    const close = text.indexOf(marker, open + marker.length)
    if (close === -1 || close === open + marker.length) continue // unmatched or empty: literal
    best = { marker, open, close }
  }
  return best
}

/** @param {string} text @param {Node} into */
function inline(text, into) {
  let rest = text
  for (let pair = findPair(rest); pair; pair = findPair(rest)) {
    if (pair.open > 0) into.append(document.createTextNode(rest.slice(0, pair.open)))
    const [tag, className] = INLINE[pair.marker]
    const element = document.createElement(tag)
    element.className = className
    inline(rest.slice(pair.open + pair.marker.length, pair.close), element)
    into.append(element)
    rest = rest.slice(pair.close + pair.marker.length)
  }
  if (rest) into.append(document.createTextNode(rest))
}

/** a paragraph's lines, with <br> between them */
function lines(chunk, into) {
  chunk.forEach((line, index) => {
    if (index) into.append(document.createElement('br'))
    inline(line, into)
  })
}

/**
 * @param {unknown} source
 * @returns {DocumentFragment}
 */
export function richText(source) {
  const fragment = document.createDocumentFragment()
  const text = typeof source === 'string' ? source.replaceAll('\r\n', '\n').trim() : ''
  if (!text) return fragment
  let paragraph = []
  let list = null
  const flushParagraph = () => {
    if (!paragraph.length) return
    const element = document.createElement('p')
    lines(paragraph, element)
    fragment.append(element)
    paragraph = []
  }
  for (const raw of text.split('\n')) {
    const line = raw.trim()
    if (!line) {
      flushParagraph()
      list = null
    } else if (BULLET.test(line)) {
      flushParagraph()
      if (!list) {
        list = document.createElement('ul')
        fragment.append(list)
      }
      const item = document.createElement('li')
      inline(line.replace(BULLET, ''), item)
      list.append(item)
    } else {
      list = null
      paragraph.push(line)
    }
  }
  flushParagraph()
  return fragment
}

/** Replace everything in `element` with the rendered text. @param {Element} element @param {unknown} source */
export function renderRich(element, source) {
  element.replaceChildren(richText(source))
}
